import { configureStore, type Dispatch, type UnknownAction } from '@reduxjs/toolkit';
import { configureSession } from '@qcdoc/mock-runtime/session.ts';
import auth, { authActions } from '../../redux/slices/authSlice.ts';
import app, { appActions } from '../../redux/slices/appSlice.ts';
import theme, { themeActions } from '../../redux/slices/themeSlice.ts';
import type { CommonState } from '../../redux/selectors/commonSelectors.ts';
import { persistAuth } from './persistence.ts';
import { bindAccessTokenReader, setAccessToken } from './token.ts';

export const commonReducers = { auth, app, theme };

export interface CommonStore {
  getState: () => CommonState;
  dispatch: Dispatch<UnknownAction>;
  subscribe: (listener: () => void) => () => void;
}

let activeStore: CommonStore | undefined;
let fallbackStore = false;
let unsubscribe: (() => void) | undefined;

export function bindCommonStore(store: CommonStore): () => void {
  if (activeStore === store) return () => {};
  const previous = fallbackStore ? activeStore?.getState() : undefined;
  unsubscribe?.();
  activeStore = store;
  fallbackStore = false;
  if (previous) {
    store.dispatch(authActions.patch(previous.auth));
    store.dispatch(appActions.patch(previous.app));
    store.dispatch(themeActions.patch(previous.theme));
  }
  let last = store.getState();
  const syncTheme = () => {
    if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', store.getState().theme.mode);
  };
  syncTheme();
  unsubscribe = store.subscribe(() => {
    const next = store.getState();
    if (next.auth !== last.auth) {
      persistAuth(next.auth);
      if (!next.auth.user && !next.auth.accessToken) setAccessToken(null);
    }
    try {
      if (next.app !== last.app) {
        const { selectedPsl, sidebarCollapsed, sidebarWidth } = next.app;
        localStorage.setItem('qcdoc-app-store', JSON.stringify({ state: { selectedPsl, sidebarCollapsed, sidebarWidth }, version: 0 }));
      }
      if (next.theme !== last.theme) localStorage.setItem('qcdoc-theme', JSON.stringify({ state: next.theme, version: 0 }));
    } catch {
      syncTheme();
    }
    if (next.theme !== last.theme) syncTheme();
    last = next;
  });
  const stop = unsubscribe;
  configureSession(readAuthSession);
  bindAccessTokenReader(() => readAuthSession().accessToken);
  return () => {
    stop();
    if (activeStore === store) {
      activeStore = undefined;
      unsubscribe = undefined;
    }
  };
}

export function createCommonStore() {
  const store = configureStore({ reducer: commonReducers, devTools: false });
  bindCommonStore(store);
  return store;
}

export function getCommonStore(): CommonStore {
  if (!activeStore) {
    createCommonStore();
    fallbackStore = true;
  }
  return activeStore!;
}

export function readAuthSession() {
  return getCommonStore().getState().auth;
}

configureSession(readAuthSession);
bindAccessTokenReader(() => readAuthSession().accessToken);